import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import UiPanel from './UiPanel';
import UiDateField from './UiDateField';
import UiTimeField from './UiTimeField';

const TONE_STYLES = {
  primary: {
    select: 'focus:border-primary-500 focus:ring-primary-100',
    date: 'focus:border-primary-500 focus:ring-primary-100',
    time: 'focus:border-primary-500 focus:ring-primary-100',
    button: 'hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50',
    icon: 'text-primary-600',
  },
  orange: {
    select: 'border-orange-200 focus:border-orange-500 focus:ring-orange-100',
    date: 'border-orange-200 focus:border-orange-500 focus:ring-orange-100',
    time: 'border-orange-200 focus:border-orange-500 focus:ring-orange-100',
    button: 'border-orange-200 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50',
    icon: 'text-orange-600',
  },
  red: {
    select: 'focus:border-red-500 focus:ring-red-100',
    date: 'focus:border-red-500 focus:ring-red-100',
    time: 'focus:border-red-500 focus:ring-red-100',
    button: 'hover:border-red-300 hover:text-red-600 hover:bg-red-50',
    icon: 'text-red-600',
  },
};

const parseIsoDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toIsoDate = (date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const getPeriodMode = (preset) => {
  if (preset === 'month' || preset === 'this_month') return 'month';
  if (preset === 'year' || preset === 'this_year') return 'year';
  return null;
};

const UiDateRangeBlock = ({
  preset,
  presetOptions = [],
  onPresetChange,
  onPrevious,
  onNext,
  fromDate,
  onFromDateChange,
  toDate,
  onToDateChange,
  fromTime = '',
  onFromTimeChange,
  toTime = '',
  onToTimeChange,
  showTime = true,
  fromLabel = 'From',
  toLabel = 'To',
  fromTimeLabel = 'From time',
  toTimeLabel = 'To time',
  rangeText = '',
  header = null,
  footer = null,
  children = null,
  className = '',
  bodyClassName = '',
  tone = 'primary',
  dateFieldClassName = 'min-w-[15rem]',
  timeFieldClassName = 'w-[9rem]',
  dateDisabled = false,
  timeDisabled = false,
  periodPicker = false,
  onPeriodChange,
}) => {
  const toneStyles = TONE_STYLES[tone] || TONE_STYLES.primary;
  const fieldLabelClass = 'text-xs font-semibold uppercase tracking-wide text-gray-500';
  const periodMode = periodPicker ? getPeriodMode(preset) : null;
  const periodDate = parseIsoDate(fromDate) || new Date();
  const monthValue = `${periodDate.getFullYear()}-${String(periodDate.getMonth() + 1).padStart(2, '0')}`;
  const yearValue = String(periodDate.getFullYear());
  const applyPeriod = (mode, value) => {
    if (!value) return;
    let start;
    let end;
    if (mode === 'year') {
      const year = Number(value);
      if (!year || year < 1900) return;
      start = new Date(year, 0, 1);
      end = new Date(year, 11, 31);
    } else {
      const [year, month] = String(value).split('-').map(Number);
      if (!year || !month) return;
      start = new Date(year, month - 1, 1);
      end = new Date(year, month, 0);
    }
    const startDate = toIsoDate(start);
    const endDate = toIsoDate(end);
    const displayEndDate = toIsoDate(addDays(end, 1));
    onPeriodChange?.({
      mode,
      preset,
      startDate,
      endDate,
      displayEndDate,
      year: start.getFullYear(),
      month: start.getMonth(),
      monthValue: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`,
    });
    if (!onPeriodChange) {
      onFromDateChange?.(startDate);
      onToDateChange?.(displayEndDate);
    }
  };

  return (
    <UiPanel className={className} bodyClassName={`space-y-3 p-4 ${bodyClassName}`}>
      {header}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center rounded-xl border border-gray-200 bg-white shadow-sm">
          <button
            type="button"
            onClick={onPrevious}
            className={`rounded-l-xl border-r border-gray-200 px-3 py-2 text-gray-500 transition ${toneStyles.button}`}
            aria-label="Previous range"
          >
            <ChevronLeft size={16} />
          </button>
          <select
            value={preset}
            onChange={(event) => onPresetChange?.(event.target.value)}
            className={`min-w-[9rem] rounded-none border-0 bg-transparent px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-0 ${toneStyles.select}`}
          >
            {presetOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={onNext}
            className={`rounded-r-xl border-l border-gray-200 px-3 py-2 text-gray-500 transition ${toneStyles.button}`}
            aria-label="Next range"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {periodMode ? (
          <div className="flex min-w-[16rem] flex-1 flex-wrap items-center gap-2">
            <span className={fieldLabelClass}>{periodMode === 'year' ? 'Year' : 'Month'}</span>
            {periodMode === 'year' ? (
              <input
                type="number"
                min="1900"
                max="2200"
                value={yearValue}
                onChange={(event) => applyPeriod('year', event.target.value)}
                className={`min-w-[9rem] rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 ${toneStyles.date}`}
                disabled={dateDisabled}
              />
            ) : (
              <input
                type="month"
                value={monthValue}
                onChange={(event) => applyPeriod('month', event.target.value)}
                className={`min-w-[11rem] rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 ${toneStyles.date}`}
                disabled={dateDisabled}
              />
            )}
            {showTime ? <span className="text-xs text-gray-400">Business day {fromTime} to {toTime}</span> : null}
          </div>
        ) : (
          <>
            <div className="flex min-w-[16rem] flex-1 flex-wrap items-center gap-2">
              <span className={fieldLabelClass}>{fromLabel}</span>
              <UiDateField
                value={fromDate}
                onChange={onFromDateChange}
                className={dateFieldClassName}
                inputClassName={toneStyles.date}
                disabled={dateDisabled}
              />
              {showTime ? (
                <>
                  <span className={fieldLabelClass}>{fromTimeLabel}</span>
                  <UiTimeField
                    value={fromTime}
                    onChange={onFromTimeChange}
                    className={timeFieldClassName}
                    inputClassName={toneStyles.time}
                    disabled={timeDisabled}
                  />
                </>
              ) : null}
            </div>

            <div className="flex min-w-[16rem] flex-1 flex-wrap items-center gap-2">
              <span className={fieldLabelClass}>{toLabel}</span>
              <UiDateField
                value={toDate}
                onChange={onToDateChange}
                className={dateFieldClassName}
                inputClassName={toneStyles.date}
                disabled={dateDisabled}
              />
              {showTime ? (
                <>
                  <span className={fieldLabelClass}>{toTimeLabel}</span>
                  <UiTimeField
                    value={toTime}
                    onChange={onToTimeChange}
                    className={timeFieldClassName}
                    inputClassName={toneStyles.time}
                    disabled={timeDisabled}
                  />
                </>
              ) : null}
            </div>
          </>
        )}
      </div>

      {rangeText ? <p className="text-xs text-gray-500">{rangeText}</p> : null}
      {children}
      {footer}
    </UiPanel>
  );
};

export default UiDateRangeBlock;
